import { useMemo } from 'react';
import { useDcRuntimeByWoDetailGlobalId } from '@api/bff/calibrationRuntime/queries/useDcRuntimeByWoDetailGlobalId';
import { useDcRuntime } from '@api/bff/calibrationRuntime/queries/useDcRuntime';
import type {
  CalibrationRuntimeData,
  CalibrationRuntimeReadResult
} from './types';

type UseCalibrationRuntimeReadParams = {
  woDetailGlobalId?: string;
  dcRuntimeId?: string;
  enabled?: boolean;
};

const parseCalibrationData = (
  jsonData: string | null | undefined
): CalibrationRuntimeData | null => {
  if (!jsonData) return null;

  try {
    const parsedData = JSON.parse(jsonData) as CalibrationRuntimeData;

    if (
      parsedData?.tests?.section?.maxLoad !== undefined &&
      parsedData?.tests?.section?.readability
    ) {
      const readability = parsedData.tests.section.readability;
      const decimalPlaces =
        readability < 1 ? Math.abs(Math.floor(Math.log10(readability))) : 0;
      parsedData.tests.section.maxLoad =
        Math.round(
          parsedData.tests.section.maxLoad * Math.pow(10, decimalPlaces)
        ) / Math.pow(10, decimalPlaces);
    }

    return parsedData;
  } catch (error) {
    console.error('Failed to parse calibration JSON data:', error);
    return null;
  }
};

export const useCalibrationRuntimeRead = ({
  woDetailGlobalId,
  dcRuntimeId,
  enabled = true
}: UseCalibrationRuntimeReadParams): CalibrationRuntimeReadResult => {
  const byWoDetail = useDcRuntimeByWoDetailGlobalId({
    woDetailGlobalId: woDetailGlobalId ?? '',
    enabled: enabled && !!woDetailGlobalId && !dcRuntimeId
  });

  const byId = useDcRuntime(dcRuntimeId ?? '', enabled && !!dcRuntimeId);

  const source = dcRuntimeId ? byId : byWoDetail;
  const calibrationRuntime = source.data;

  const calibrationData = useMemo<CalibrationRuntimeData | null>(
    () => parseCalibrationData(calibrationRuntime?.jsonData),
    [calibrationRuntime?.jsonData]
  );

  return {
    calibrationExists: !!calibrationRuntime,
    calibrationRuntime,
    calibrationData,
    isCheckingCalibration: source.isLoading,
    error: source.error as Error | null,
    refetch: source.refetch
  };
};

export const useCalibrationRuntimeByGuid = (
  id: string | undefined,
  enabled = true
) => {
  const {
    data: calibrationRuntime,
    isLoading,
    error
  } = useDcRuntime(id ?? '', enabled && !!id);

  const calibrationData = useMemo<CalibrationRuntimeData | null>(
    () => parseCalibrationData(calibrationRuntime?.jsonData),
    [calibrationRuntime?.jsonData]
  );

  return {
    calibrationRuntime,
    calibrationData,
    isLoading,
    error: error as Error | null
  };
};
